import type { UIMessage } from 'ai';
import { continuationChipText } from './source-chip';

/**
 * Markdown serialization for "Save as Markdown" (FRONT-223).
 *
 * Two entry points in the chat use these helpers:
 *  - the per-response "Save as document" action (single assistant reply), and
 *  - the header "Save conversation" action (the whole thread).
 *
 * Both POST to `/api/documents`, which stores a `.md` document that surfaces
 * in the user's documents, Files, and inbox. These functions are pure (no
 * DOM, no clock) so they're trivially unit-testable.
 */

/**
 * Flatten a message's text parts into a single string. The server-started
 * late-approval continuation yields its chip wording, never the instruction
 * it sent on the person's behalf (FRONT-3036); every other message, button
 * sources included, exports its own text as before.
 */
function messageText(message: UIMessage): string {
  const chip = message.role === 'user' ? continuationChipText(message) : null;
  if (chip != null) return chip;
  return message.parts
    .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
    .map((p) => p.text)
    .join('');
}

/**
 * Strip the `[Skill: …]` / `[run: N]` marker lines the skill runner prepends
 * to sourced briefings (see `research-briefing.ts`'s extractResearchBriefing).
 * Those markers are an internal round-trip channel, not something a reader
 * should see in an exported document. Leaves ordinary replies untouched.
 */
function stripBriefingMarkers(text: string): string {
  const lines = text.split('\n');
  if (!/^\[Skill:\s*[^\]]+\]\s*$/.test(lines[0] ?? '')) return text;
  let i = 1;
  if (/^\[run:\s*\d+\]\s*$/.test(lines[i] ?? '')) i += 1;
  if (lines[i] === '') i += 1;
  return lines.slice(i).join('\n');
}

/**
 * Derive a document title from a response's text — the first markdown heading
 * if present (replies often open with a conversational line that makes a poor
 * title), else the first non-empty line, stripped of markdown markers and
 * capped. Falls back to `fallback` when the text has no usable line.
 */
export function deriveTitle(text: string, fallback: string): string {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const heading = lines.find((l) => /^#{1,6}\s+/.test(l));
  const pick = heading ?? lines[0] ?? '';
  const cleaned = pick
    .replace(/^#{1,6}\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/^[-*]\s+/, '')
    .replace(/:$/, '')
    .trim();
  if (!cleaned) return `${fallback} — note`;
  return cleaned.length > 80 ? `${cleaned.slice(0, 79)}…` : cleaned;
}

/**
 * Title for a whole-conversation export: derived from the first user message
 * (the prompt that opened the thread), falling back to a generic label.
 */
export function deriveConversationTitle(
  messages: UIMessage[],
  fallback = 'Chat conversation',
): string {
  const firstUser = messages.find((m) => m.role === 'user');
  const text = firstUser ? messageText(firstUser) : '';
  return deriveTitle(text, fallback);
}

export interface ConversationMarkdownOptions {
  /** Heading label for the human's turns. Defaults to "You". */
  userLabel?: string;
  /** Heading label for the agent's turns. Defaults to "Eddy". */
  assistantLabel?: string;
  /** Optional top-level `# title` line prepended to the document. */
  title?: string;
}

/**
 * Markdown for the renderable artifacts a turn produced via tools — so a saved
 * document keeps the image (or app) it generated, not just the prose. The text
 * serializer drops tool-result cards by design (debug noise); this brings back
 * the ones a reader actually wants. Reusable across both save paths; add a case
 * per artifact-producing tool.
 */
/**
 * Escape text used as a markdown link/image label. The label comes from a
 * user-controlled prompt or app title, so a `[`, `]`, or newline would break
 * out of the `[label]` / `![alt]` syntax in the saved document. Backslash is
 * escaped first so we don't double-escape, then `[`/`]`, then newlines flatten
 * to spaces.
 */
function escapeMarkdownLabel(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/([[\]])/g, '\\$1')
    .replace(/\r?\n/g, ' ');
}

/**
 * Encode a URL for safe interpolation into `](url)`. `(` / `)` (and spaces)
 * would otherwise terminate the markdown link early; encodeURI handles the
 * rest. Internal paths like `/api/files/<id>/raw` pass through unchanged.
 */
function encodeMarkdownUrl(url: string): string {
  return encodeURI(url).replace(/\(/g, '%28').replace(/\)/g, '%29');
}

/** Markdown image for a `generate_image` tool output, or '' if it has none. */
function renderGeneratedImage(o: Record<string, unknown>): string {
  if (!o.fileId && !o.url) return '';
  const src = typeof o.url === 'string' ? o.url : `/api/files/${String(o.fileId)}/raw`;
  const alt = escapeMarkdownLabel(
    String(o.revisedPrompt ?? o.prompt ?? 'Generated image').slice(0, 200),
  );
  return `![${alt}](${encodeMarkdownUrl(src)})`;
}

/** Markdown link for an `app_publish_html` tool output, or '' if no app slug. */
function renderPublishedApp(o: Record<string, unknown>): string {
  const app = o.app as Record<string, unknown> | undefined;
  if (!app?.slug) return '';
  const url = (app.url as string) ?? (app.path as string) ?? `/apps/${String(app.slug)}/`;
  const label = escapeMarkdownLabel(String(app.title ?? app.slug));
  return `[${label}](${encodeMarkdownUrl(url)})`;
}

export function messageArtifactsMarkdown(message: UIMessage): string {
  const out: string[] = [];
  for (const part of message.parts) {
    const p = part as { type?: string; toolName?: string; output?: unknown };
    const type = typeof p.type === 'string' ? p.type : '';
    if (type !== 'dynamic-tool' && !type.startsWith('tool-')) continue;
    if (!p.output || typeof p.output !== 'object') continue;
    const name = p.toolName ?? type.replace('tool-', '');
    const o = p.output as Record<string, unknown>;
    const md =
      name === 'generate_image'
        ? renderGeneratedImage(o)
        : name === 'app_publish_html'
          ? renderPublishedApp(o)
          : '';
    if (md) out.push(md);
  }
  return out.join('\n\n');
}

/**
 * Serialize an ordered list of chat messages into a single clean Markdown
 * document — user and assistant turns only, each under a `## <label>` heading.
 * System status pills, tool-call cards, and reasoning blocks are intentionally
 * dropped so the export reads as a shareable transcript rather than a debug
 * trace. Empty turns (e.g. a bubble that only held a tool call) are skipped.
 */
export function conversationToMarkdown(
  messages: UIMessage[],
  opts: ConversationMarkdownOptions = {},
): string {
  const userLabel = opts.userLabel ?? 'You';
  const assistantLabel = opts.assistantLabel ?? 'EDDY';
  const sections: string[] = [];
  if (opts.title) sections.push(`# ${opts.title}`);
  for (const message of messages) {
    if (message.role !== 'user' && message.role !== 'assistant') continue;
    let text = messageText(message).trim();
    if (message.role === 'assistant') text = stripBriefingMarkers(text).trim();
    // Keep generated artifacts (images, published apps) in the export, not just
    // the prose — otherwise a turn whose whole point was an image saves empty.
    const artifacts =
      message.role === 'assistant' ? messageArtifactsMarkdown(message) : '';
    const body = [text, artifacts].filter(Boolean).join('\n\n');
    if (!body) continue;
    const label = message.role === 'user' ? userLabel : assistantLabel;
    sections.push(`## ${label}\n\n${body}`);
  }
  return sections.join('\n\n');
}
